'use client';

import { useState, useEffect } from 'react';
import {
  setCookie,
  hasCookieProperty,
  trackEvent,
  TRACKING_CATEGORIES,
  TRACKING_ACTIONS,
  TRACKING_LABELS,
  COOKIE_NAMES,
  CLIENT_COOKIE_CONFIG,
  scrollToElementIntoView,
} from 'utils';
import { Dialog, DIALOG_TYPE } from '../../../dialog';
import { DownloadVoucherCampaign } from './DownloadVoucherCampaign';
import type { DownloadVoucherCampaignProps } from '../types';

type DownloadVoucherCampaignDialogProps = Omit<DownloadVoucherCampaignProps, 'openInDialog' | 'onInstallClick'>;

/**
 * DownloadVoucherCampaignDialog - Modal wrapper for DownloadVoucherCampaign
 *
 * Shows voucher campaign in a dialog 2 seconds after page load.
 * Uses cookies to ensure dialog appears only once per user.
 * Cookie is set when dialog is closed, preventing duplicate sets.
 * Cookie can be read server-side for SSR optimization.
 *
 * Features:
 * - Delayed appearance (2 seconds)
 * - Cookie-based "show once" logic (readable server-side)
 * - Cookie set only on dialog close (not on open)
 * - Installation guide button handler (client-side)
 */
export function DownloadVoucherCampaignDialog(props: DownloadVoucherCampaignDialogProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Check if dialog already shown (cookie value must be 'true')
    const isShown = hasCookieProperty(COOKIE_NAMES.DOWNLOAD_DIALOG_SHOWN, 'true');

    if (!isShown) {
      // Set cookie to 'false' by default if it doesn't exist
      if (!hasCookieProperty(COOKIE_NAMES.DOWNLOAD_DIALOG_SHOWN)) {
        setCookie(COOKIE_NAMES.DOWNLOAD_DIALOG_SHOWN, 'false', CLIENT_COOKIE_CONFIG.DOWNLOAD_DIALOG_SHOWN);
      }

      // Show dialog after 2 seconds
      const timer = setTimeout(() => {
        setOpen(true);
        // Track dialog show event (GA + Jentis)
        trackEvent(TRACKING_CATEGORIES.FOTOBUCH, TRACKING_ACTIONS.DL_MODAL, TRACKING_LABELS.SHOW);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);

    // Track dialog close event (GA + Jentis)
    trackEvent(TRACKING_CATEGORIES.FOTOBUCH, TRACKING_ACTIONS.DL_MODAL, TRACKING_LABELS.CLOSE);

    // Set cookie to 'true' when dialog is closed
    setCookie(COOKIE_NAMES.DOWNLOAD_DIALOG_SHOWN, 'true', CLIENT_COOKIE_CONFIG.DOWNLOAD_DIALOG_SHOWN);
  };

  const handleInstallClick = () => {
    // Close dialog
    setOpen(false);

    // Scroll to first download steps article without adding URL hash
    const firstDownloadStepsSection = document.querySelector('[data-platform-key]');

    if (firstDownloadStepsSection) {
      const sectionId = firstDownloadStepsSection.getAttribute('id');

      if (sectionId) {
        scrollToElementIntoView(sectionId, false);
      }
    }
  };

  return (
    <Dialog
      className="download-voucher-campaign__dialog"
      isOpen={open}
      onClose={handleClose}
      dialogType={DIALOG_TYPE.SOFTWARE_DOWNLOAD}
    >
      <DownloadVoucherCampaign {...props} openInDialog onInstallClick={handleInstallClick} />
    </Dialog>
  );
}
